import { useEffect, useRef, useCallback, useMemo } from 'react';
import {
  User, FileText, Building2, Users, Briefcase, CheckSquare,
  Receipt, BookOpen, Settings, FileDown, BarChart3, FileCode,
  Calculator, Loader2, Search
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { SearchResult, SearchSectionId, SEARCH_SECTIONS, getNavigationTarget } from '@/hooks/useGlobalSearch';

interface GlobalSearchResultsProps {
  results: SearchResult[];
  groupedResults: Record<SearchSectionId, SearchResult[]>;
  isLoading: boolean;
  searchTerm: string;
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  onResultClick: (section: string, params?: any) => void;
  onClose: () => void;
}

// Icon mapping for each section type
const SECTION_ICONS: Record<SearchSectionId, typeof User> = {
  kunden: User,
  eigenvertraege: FileText,
  fremdvertraege: FileText,
  gesellschaften: Building2,
  ansprechpartner: Users,
  zugaenge: Settings,
  vermittlernummern: FileCode,
  rabatte: Receipt,
  leads: Briefcase,
  aufgaben: CheckSquare,
  downloadcenter: FileDown,
  berichte: BarChart3,
  vorlagen: FileCode,
  buchhaltung: Calculator,
  vertriebspartner: Users,
  elearning: BookOpen,
  abrechnungen: Receipt,
  einstellungen: Settings,
};

// Display labels for sections
const SECTION_LABELS: Record<SearchSectionId, string> = {
  kunden: 'Kunden',
  eigenvertraege: 'Eigenverträge',
  fremdvertraege: 'Fremdverträge',
  gesellschaften: 'Gesellschaften',
  ansprechpartner: 'Ansprechpartner',
  zugaenge: 'Zugänge',
  vermittlernummern: 'Vermittlernummern',
  rabatte: 'Rabatte',
  leads: 'Leads',
  aufgaben: 'Aufgaben',
  downloadcenter: 'Downloadcenter',
  berichte: 'Berichte',
  vorlagen: 'Vorlagen',
  buchhaltung: 'Buchhaltung',
  vertriebspartner: 'Vertriebspartner',
  elearning: 'E-Learning',
  abrechnungen: 'Abrechnungen',
  einstellungen: 'Einstellungen',
};

// Order for displaying result groups
const SECTION_ORDER: SearchSectionId[] = [
  'kunden',
  'leads',
  'eigenvertraege',
  'fremdvertraege',
  'aufgaben',
  'gesellschaften',
  'ansprechpartner',
  'vertriebspartner',
  'abrechnungen',
  'buchhaltung',
  'elearning',
  'downloadcenter',
  'berichte',
  'vorlagen',
  'zugaenge',
  'vermittlernummern',
  'rabatte',
  'einstellungen',
];

export function GlobalSearchResults({
  results,
  groupedResults,
  isLoading,
  searchTerm,
  selectedIndex,
  onSelectedIndexChange,
  onResultClick,
  onClose,
}: GlobalSearchResultsProps) {
  const resultsRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Flatten results in display order for keyboard navigation
  const flatResults = useMemo(() => {
    const flat: SearchResult[] = [];
    SECTION_ORDER.forEach(sectionId => {
      const sectionResults = groupedResults[sectionId];
      if (sectionResults?.length) {
        flat.push(...sectionResults);
      }
    });
    return flat;
  }, [groupedResults]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (flatResults.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          onSelectedIndexChange(Math.min(selectedIndex + 1, flatResults.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          onSelectedIndexChange(Math.max(selectedIndex - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[selectedIndex]) {
            handleResultSelect(flatResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [flatResults, selectedIndex, onSelectedIndexChange, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = itemRefs.current.get(selectedIndex);
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const handleResultSelect = useCallback((result: SearchResult) => {
    const { section, params } = getNavigationTarget(result);
    onResultClick(section, params);
  }, [onResultClick]);

  const setItemRef = useCallback((index: number) => (el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(index, el);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);

  // Track global index for keyboard navigation
  let globalIndex = 0;

  return (
    <div ref={resultsRef} className="max-h-[500px] overflow-hidden">
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* No Results */}
      {!isLoading && searchTerm && results.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">
          <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Keine Ergebnisse für "{searchTerm}"</p>
        </div>
      )}

      {/* Empty State - No Search Term */}
      {!isLoading && !searchTerm && (
        <div className="py-8 px-4 space-y-4">
          <div className="text-center text-sm text-muted-foreground">
            Durchsuchen Sie alle Daten im CRM
          </div>

          <div className="space-y-2 text-xs text-muted-foreground px-4">
            <p className="font-medium">Beispiele:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Kunde: "Müller" oder "max@example.com"</li>
              <li>Vertrag: "2024-123" oder Polizzennummer</li>
              <li>Lead: Telefonnummer oder Unternehmen</li>
              <li>Partner: Name oder E-Mail</li>
            </ul>
          </div>

          <div className="space-y-2 text-xs text-muted-foreground px-4">
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 rounded border bg-muted">↑↓</kbd>
              <span>Navigation</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 rounded border bg-muted">Enter</kbd>
              <span>Öffnen</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 rounded border bg-muted">Esc</kbd>
              <span>Schließen</span>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {!isLoading && results.length > 0 && (
        <ScrollArea className="h-[500px]">
          <div className="p-2 space-y-3">
            {SECTION_ORDER.map(sectionId => {
              const sectionResults = groupedResults[sectionId];
              if (!sectionResults?.length) return null;

              const SectionIcon = SECTION_ICONS[sectionId];
              const sectionLabel = SECTION_LABELS[sectionId];
              const startIndex = globalIndex;

              return (
                <div key={sectionId}>
                  {/* Section Header */}
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <SectionIcon className="h-3.5 w-3.5" />
                    {sectionLabel}
                    <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                      {sectionResults.length}
                    </Badge>
                  </div>

                  {/* Section Results */}
                  <div className="space-y-0.5">
                    {sectionResults.map((result, idx) => {
                      const currentIndex = startIndex + idx;
                      globalIndex++;
                      const isSelected = selectedIndex === currentIndex;

                      return (
                        <div
                          key={result.id}
                          ref={setItemRef(currentIndex)}
                          onClick={() => handleResultSelect(result)}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors",
                            isSelected
                              ? "bg-primary/10 text-foreground"
                              : "hover:bg-muted/50"
                          )}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{result.title}</span>
                              {result.badge && (
                                <Badge variant="secondary" className="text-xs shrink-0">
                                  {result.badge}
                                </Badge>
                              )}
                            </div>
                            {result.subtitle && (
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {result.subtitle}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
